// ─────────────────────────────────────────────────────────────
// SHARED INSTRUMENT TABLE — used by both the Measurement Chain and
// DUT Chain tabs. Supports reordering rows (Sort Order) by DRAGGING a
// row via its drag handle (⠿) in the leftmost column, using native
// HTML5 drag-and-drop — no extra libraries required. Pass
// `onReorder(dragId, dropId)` to enable it; omit it to hide the
// handle/disable dragging (e.g. for tables that don't need ordering).
//
// Moved verbatim out of the original monolithic MPCreationIndex.js —
// no UI or behavior changes.
// ─────────────────────────────────────────────────────────────
import { useState } from "react";
import { Btn, TD, Empty, AutoComplete, eiCls } from "./mpShared";
import {
  hasPrivilege,
  PRIVILEGES,
  isSmileIntegrated,
} from "../../../Common/UIComponents";

function InstrumentTable({
  rows,
  onRemove,
  onUpdate,
  onReorder,
  emptyIcon,
  emptyMsg,
  emptySub,
  instrumentsList,
  onInstrumentChange,
  selectedInstrumentIds,
  // When true (app running embedded inside Smile), the Reference No
  // column becomes mandatory for every meaningful row — same
  // isSmileIntegrated() convention used across the rest of the MP
  // tabs (Test Info, Communication/Result, etc).
  requireRefId = false,
  // Map of row._id -> { refId: "error message" }, used to highlight
  // rows missing a mandatory Reference No on Save/Submit, the same
  // way every other MP field surfaces validation errors.
  rowErrors = {},
}) {
  const reorderable = !!onReorder;
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const isFormSmil = localStorage.getItem("isFromSmile") == 0;

  const handleDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires data to be set for drag to initiate
    e.dataTransfer.setData("text/plain", String(id));
  };
  const handleDragOver = (e, id) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (id !== overId) setOverId(id);
  };
  const handleDragLeave = (id) => {
    setOverId((cur) => (cur === id ? null : cur));
  };
  const handleDrop = (e, id) => {
    e.preventDefault();
    try {
      if (dragId != null && dragId !== id) onReorder(dragId, id);
    } catch (err) {
      console.error("Error reordering instrument rows:", err);
    }
    setDragId(null);
    setOverId(null);
  };
  const handleDragEnd = () => {
    setDragId(null);
    setOverId(null);
  };

  const refNoLabel = requireRefId ? "Reference No *" : "Reference No";

  const cols = reorderable
    ? [
        "",
        "S.No",
        refNoLabel,
        "Name of Instruments / Accessories",
        "Manufacturer",
        "Model",
        ...(!isFormSmil ? ["Calibration / Verification Due Date"] : []),
        ...(hasPrivilege(PRIVILEGES.MP_DELETE) ? ["Options"] : []),
      ]
    : [
        "S.No",
        refNoLabel,
        "Name of Instruments / Accessories",
        "Manufacturer",
        "Model",
        ...(!isFormSmil ? ["Calibration / Verification Due Date"] : []),
        ...(hasPrivilege(PRIVILEGES.MP_DELETE) ? ["Options"] : []),
      ];

  return (
    <div className="rounded-xl border border-[#e5e9e9] w-full max-w-full overflow-x-auto">
      <table className="w-full min-w-[900px] border-collapse text-[11.5px]">
        <thead>
          <tr>
            {cols?.map((h, i) => (
              <th
                key={`${h}-${i}`}
                style={reorderable && i === 0 ? { width: 30 } : undefined}
                className="bg-[#f1f4f4] px-4 py-3 text-left text-[#3e494a] font-bold text-[11px] uppercase tracking-wider whitespace-nowrap border-b border-[#ebeeee]"
              >
                {h === refNoLabel && requireRefId ? (
                  <>
                    Reference No <span className="text-red-500">*</span>
                  </>
                ) : (
                  h
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows?.length === 0 ? (
            <tr>
              <td colSpan={reorderable ? 8 : 7}>
                <Empty icon={emptyIcon} msg={emptyMsg} sub={emptySub} />
              </td>
            </tr>
          ) : (
            rows?.map((row, idx) => {
              // Check if an instrument is selected for this row
              const isInstrumentSelected = Boolean(row.refId?.value);

              // Filter out already selected instruments, but keep the current row's selection visible
              const availableInstruments = instrumentsList?.filter(
                (i) =>
                  !selectedInstrumentIds.includes(i.Instrument_ID) ||
                  i.Instrument_ID === row.refId?.value,
              );
              return (
                <tr
                  key={row._id}
                  onDragOver={
                    reorderable ? (e) => handleDragOver(e, row._id) : undefined
                  }
                  onDragLeave={
                    reorderable ? () => handleDragLeave(row._id) : undefined
                  }
                  onDrop={
                    reorderable ? (e) => handleDrop(e, row._id) : undefined
                  }
                  className={`transition-colors
                  ${idx % 2 === 0 ? "bg-white" : "bg-[#fafcfc]"}
                  ${dragId === row._id ? "opacity-40" : ""}
                  ${reorderable && overId === row._id && dragId !== row._id ? "outline outline-2 outline-[#08757C] outline-offset-[-2px]" : ""}`}
                >
                  {reorderable && (
                    <TD cls="text-center w-8 px-1">
                      <span
                        draggable
                        onDragStart={(e) => handleDragStart(e, row._id)}
                        onDragEnd={handleDragEnd}
                        title="Drag to reorder"
                        className="inline-flex items-center justify-center h-7 w-7 rounded-lg text-[#9eabac] hover:text-[#08757C] hover:bg-[#f1f4f4] cursor-grab active:cursor-grabbing select-none"
                      >
                        <span className="material-symbols-outlined text-[16px]">
                          drag_indicator
                        </span>
                      </span>
                    </TD>
                  )}
                  <TD cls="text-center font-bold text-[#c0cbcc] w-10">
                    {idx + 1}
                  </TD>
                  <TD style={{ minWidth: 180 }}>
                    <AutoComplete
                      value={row.refId}
                      onChange={(v) => onInstrumentChange(row, v, "refId")}
                      options={availableInstruments?.map((i) => ({
                        label: i.Instrument_No,
                        value: i.Instrument_ID,
                      }))}
                      placeholder="Select"
                      error={rowErrors[row._id]?.refId}
                    />
                    {rowErrors[row._id]?.refId && (
                      <p className="text-xs text-red-500 mt-1">
                        {rowErrors[row._id].refId}
                      </p>
                    )}
                  </TD>
                  <TD>
                    <input
                      className={`${eiCls} border`}
                      value={row.name}
                      onChange={(e) =>
                        onUpdate(row._id, "name", e.target.value)
                      }
                      placeholder="Instrument name"
                      disabled={row.calDate !== "" || isSmileIntegrated()}
                    />
                  </TD>
                  <TD>
                    <input
                      className={`${eiCls} border`}
                      value={row.manufacturer}
                      onChange={(e) =>
                        onUpdate(row._id, "manufacturer", e.target.value)
                      }
                      placeholder="Manufacturer"
                      disabled={row.calDate !== "" || isSmileIntegrated()}
                    />
                  </TD>
                  <TD>
                    <input
                      className={`${eiCls} border`}
                      value={row.model}
                      onChange={(e) =>
                        onUpdate(row._id, "model", e.target.value)
                      }
                      placeholder="Model"
                      disabled={row.calDate !== "" || isSmileIntegrated()}
                    />
                  </TD>
                  {!isFormSmil ? (
                    <TD>
                      <input
                        type="date"
                        className={`${eiCls} border`}
                        value={row.calDate}
                        onChange={(e) =>
                          onUpdate(row._id, "calDate", e.target.value)
                        }
                        disabled={row.calDate !== "" || isSmileIntegrated()}
                      />
                    </TD>
                  ) : (
                    []
                  )}
                  {hasPrivilege(PRIVILEGES.MP_DELETE) && (
                    <TD cls="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Btn
                          icon="delete"
                          onClick={() => onRemove(row)}
                          danger
                          title="Remove"
                        />
                      </div>
                    </TD>
                  )}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

// Move the row identified by `dragId` to the position of the row
// identified by `dropId` (insert-before semantics, not a straight
// swap) and return a brand-new array — used by both Measurement
// Chain and DUT Chain tabs so their Sort Order can be dragged and
// persisted.
function dragReorderRows(rows, dragId, dropId) {
  const fromIdx = rows.findIndex((r) => r._id === dragId);
  const toIdx = rows.findIndex((r) => r._id === dropId);
  if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return rows;
  const next = [...rows];
  const [moved] = next.splice(fromIdx, 1);
  next.splice(toIdx, 0, moved);
  return next;
}

// ─────────────────────────────────────────────────────────────

export { InstrumentTable, dragReorderRows };
